import { useMutation } from '@moonpig/web-core-graphql'
import { Dispatch } from 'react'
import { RegisterEmailGQL } from '../../../queries/registerEmail'
import { RegisterEmailMutation } from '../../../queries/__generated__/RegisterEmail'
import {
  genericError,
  selectContent,
  successImpression,
  track,
} from '../tracking/events'

export enum EmailCaptureError {
  INVALID_EMAIL_MESSAGE = 'INVALID_EMAIL_MESSAGE',
  GENERIC_ERROR_MESSAGE = 'GENERIC_ERROR_MESSAGE',
}

const EmailRegistrationSucceeded = () => ({
  type: 'EMAIL_REGISTERED_SUCCEEDED' as const,
})

const EmailRegistrationFailed = (errorCode: EmailCaptureError) => ({
  type: 'EMAIL_REGISTERED_FAILED' as const,
  payload: {
    errorCode,
  },
})

export type EmailRegistrationSucceeded = ReturnType<
  typeof EmailRegistrationSucceeded
>
export type EmailRegistrationFailed = ReturnType<typeof EmailRegistrationFailed>

export const useActions = (
  dispatch: Dispatch<EmailRegistrationSucceeded | EmailRegistrationFailed>,
  isModal: boolean,
) => {
  const [registerEmailMutation] =
    useMutation<RegisterEmailMutation>(RegisterEmailGQL)

  const registerEmail = async (email: string): Promise<void> => {
    track(selectContent({ isModal }))

    try {
      const { data } = await registerEmailMutation({
        variables: {
          input: {
            emailAddress: email,
          },
        },
      })

      /* istanbul ignore else */
      if (data) {
        switch (data.registerEmail.__typename) {
          case 'RegisterEmailSuccess':
            track(successImpression({ isModal }))
            dispatch(EmailRegistrationSucceeded())
            return
          case 'InvalidEmailError':
            track(
              genericError({
                isModal,
                errorReason: 'Please enter a valid email address.',
              }),
            )
            dispatch(
              EmailRegistrationFailed(EmailCaptureError.INVALID_EMAIL_MESSAGE),
            )
        }
      }
    } catch (err) {
      track(genericError({ isModal, errorReason: err.message }))
      dispatch(EmailRegistrationFailed(EmailCaptureError.GENERIC_ERROR_MESSAGE))
    }
  }

  return {
    registerEmail,
  }
}
